import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import { AlertView } from './alert';
import { useState } from 'react';
import { putImage } from '../api/images';
import { AxiosError } from 'axios';
import { getApiAccessToken } from '../auth/auth';
import { useMsal } from '@azure/msal-react';
import { Loader } from './loader';
import { ControlProps, rankWith, scopeEndsWith, JsonFormsRendererRegistryEntry } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import TextField from '@mui/material/TextField';
import ReactMarkdown from 'react-markdown';
import { SxProps } from '@mui/material';

const InputHidden = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface ImageFileControlProps extends ControlProps {}

const ImageFileControl: React.FC<ImageFileControlProps> = ({ data, handleChange, path, schema, uischema, rootSchema }) => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  
  const [errorMessage, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const validateSelectedImage = (files: FileList | null): [boolean, File | null] => {
    if (!files) {
      return [false, null];
    }

    if (files.length !== 1) {
      setError('We can only handle one image upload at a time');
      return [false, null];
    }

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      setError(`An image type is expected, we can't process: ${file.type || 'unknown file type'}`);
      return [false, null];
    }

    return [true, file];
  };

  const handleSelectedImage = async (files: FileList | null) => {
    const [valid, file] = validateSelectedImage(files);
    if (!valid || !file) {
      return;
    }

    try {
      setLoading(true);
      const accessToken = await getApiAccessToken(instance, activeAccount);
      const response = await putImage(accessToken, file);

      setLoading(false);

      const markdown = `![${file.name}](${response.url})`;
      const currentValue = data || '';
      const newValue = currentValue + (currentValue ? '\n' : '') + markdown;
      
      handleChange(path, newValue);

    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error);
        if (error?.response?.status === 500 || error?.response?.status === 502) {
          setError('Could not upload image due to server error or file size. Try compressing the image and reuploading.');
        } else {
          setError(error?.response?.data?.message || 'Failed to upload image.');
        }
      } else if (error instanceof Error) {
        console.error(error);
        setError('An unexpected error occurred during image upload.');
      }
      setLoading(false);
    }
  };

  // Custom styles for markdown preview
  const markdownSx: SxProps = {
    textAlign: 'left',
    '& h1': {
      fontSize: '1.5rem', fontWeight: 600, fontFamily: 'Inter, sans-serif', lineHeight: 1.1, mb: 0, textAlign: 'left',
    },
    '& h2': {
      fontSize: '1.2rem', fontWeight: 700, fontFamily: 'Inter, sans-serif', lineHeight: 1.1, mb: 0, textAlign: 'left',
    },
    '& h3, & h4, & h5, & h6': {
      fontSize: '1rem', fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 0, textAlign: 'left',
    },
    '& p': {
      fontSize: '0.95rem', fontFamily: 'Inter, sans-serif', lineHeight: 1.2, mb: 1, textAlign: 'left',
    },
    '& h1 + p, & h2 + p, & h3 + p, & h4 + p, & h5 + p, & h6 + p': {
      mt: 0,
    },
    '& code': {
      fontFamily: 'Fira Mono, monospace', background: '#f5f5f5', px: 0.5, fontSize: '0.95em',
    },
    '& pre': {
      background: '#f5f5f5', p: 1, fontFamily: 'Fira Mono, monospace', fontSize: '0.95em', overflow: 'auto', textAlign: 'left',
    },
    '& a': {
      color: '#1976d2', textDecoration: 'underline', fontWeight: 500,
    },
    '& ul, & ol': {
      pl: 3, mb: 1, textAlign: 'left',
    },
    '& li': {
      fontSize: '0.95rem', fontFamily: 'Inter, sans-serif', lineHeight: 1.2, textAlign: 'left',
    },
    '& blockquote': {
      borderLeft: '4px solid #DBD1F6', background: '#faf7ff', color: '#FFFFFF', pl: 2, my: 1, fontStyle: 'italic', textAlign: 'left',
    },
    '& img': {
      maxWidth: '100%', my: 1,
    },
  };

  // Custom code block renderer for react-markdown
  const components = {
    code({node, inline, className, children, ...props}: any) {
      return (
        <code style={{
          background: '#f5f5f5',
          padding: inline ? '2px 6px' : '12px',
          color: '##FFFFFF',
          fontFamily: 'Fira Mono, monospace',
          fontSize: '0.95em',
          display: inline ? 'inline' : 'block',
        }} {...props}>
          {children}
        </code>
      );
    },
    a({node, children, ...props}: any) {
      return <a style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 500 }} {...props}>{children}</a>;
    },
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', mt: 1, gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 3 }}>
          <TextField
            label="Article Text"
            value={data || ''}
            onChange={e => handleChange(path, e.target.value)}
            multiline
            minRows={3}
            sx={{ mb: 2 }}
          />
          <Button
            sx={{ p: 1, width: '50%', alignSelf: 'flex-start', marginTop: 0, marginBottom: 2 }}
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            disabled={loading}
            startIcon={<CloudUploadIcon />}
          >
            {loading ? <Loader width={20} height={20} /> : 'Upload Image'}
            <InputHidden
              type="file"
              accept="image/*"
              onChange={(event) => handleSelectedImage(event.target.files)}
            />
          </Button>
        </Box>
        <Box sx={{ ...markdownSx, p: 2, border: '1px solid #eee', borderRadius: 1, background: '#fafafa', flex: 1, minWidth: 0, maxWidth: 300, maxHeight: 300, overflow: 'auto' }}>
          <ReactMarkdown components={components}>{data || ''}</ReactMarkdown>
        </Box>
      </Box>
      <AlertView error={errorMessage} />
    </>
  );
};

export default withJsonFormsControlProps(ImageFileControl);